import React, { useContext, useMemo, useState } from "react";
import { DowntimeDisplayUnitsDropdown } from "../../../components/DowntimeDisplayUnitsDropdown/DowntimeDisplayUnitsDropdown.view";
import { LogoutButton } from "../../../components/LogoutButton";
import { HMIContext } from "../../../contexts/HMIContext/HMIContext";
import { HMICachedDataContext } from "../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { UserContext } from "../../../contexts/UserContext/UserContext";
import { MachineCenterContext } from "../../../contexts/MachineCenterContext/MachineCenterContext";
import { FailureDetails } from "./components/FailureDetails/FailureDetails.view";
import { ViewDropdown, ViewOptionsEnum } from "../../../components/ViewDropdown/ViewDropdown.view";
import { useStyles } from "./HMIDashboard.styles";
import Box from "@mui/material/Box";
import { DOWNTIMEMETHODCAPTURE, FAILUREDETAILSSTEPS } from "constants/constants";
import { ShiftDropdown } from "components/ShiftDropdown/ShiftDropdown.view";
import { Navbar } from "components/Navbar";
import { MQTTSettingDialog } from "components/MQTTSettingDialog/MQTTSettingDialog.view";
import { OEEGaugesSidebar, OEESidebarView } from "./components/OEEGaugesSidebar/OEEGaugesSidebar.view";
import { AvailabilityView, OEEView, PerformanceView, QualityView } from "./views";

const VIEW_MAP: Record<OEESidebarView, React.FC> = {
    availability: AvailabilityView,
    oee: OEEView,
    performance: PerformanceView,
    quality: QualityView,
};

export const HMIDashboard = () => {
    const { machineStatus, continueDowntimeDetailsInput } = useContext(MachineCenterContext);
    const { tokenAvailable } = useContext(UserContext);
    const { store } = useContext(HMICachedDataContext);
    const { view, failureDetailsStep } = useContext(HMIContext);
    const classes = useStyles();
    const { handleShift, shift } = useContext(HMIContext);
    const [selectedOEEView, setSelectedOEEView] = useState<OEESidebarView>(OEESidebarView.AVAILABILITY);

    const OEEModuleActive = useMemo(
        () =>
            store?.machineCenter?.machine_center_modules?.[0]?.is_enabled &&
            store?.machineCenter?.production_line?.mill?.mill_modules?.[0]?.is_enabled &&
            store?.machineCenter?.production_line?.mill?.dedicated_localserver &&
            store?.machineCenter?.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC,
        [store],
    );

    const ActiveView = useMemo(
        () => (OEEModuleActive ? VIEW_MAP[selectedOEEView] : VIEW_MAP[OEESidebarView.AVAILABILITY]),
        [selectedOEEView, OEEModuleActive],
    );

    return (
        <div className={classes.dashboardLayout}>
            <MQTTSettingDialog />
            {OEEModuleActive && <OEEGaugesSidebar selectedView={selectedOEEView} onSelectView={setSelectedOEEView} />}
            <div className={classes.mainColumn}>
                <Navbar />
                <div className={classes.contentWrapper}>
                    <Box className={classes.container}>
                        {((machineStatus && !store.isEventReportEditing && !continueDowntimeDetailsInput) ||
                            failureDetailsStep === FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME) && (
                            <div className={classes.gridContainer}>
                                <div className={classes.dropdownsRow}>
                                    <div className={classes.dropdowns}>
                                        {selectedOEEView === OEESidebarView.AVAILABILITY && (
                                            <ViewDropdown bigSize={true} />
                                        )}
                                        <ShiftDropdown handleShift={handleShift} shift={shift} bigSize={true} />
                                        {view === ViewOptionsEnum.summary &&
                                            selectedOEEView === OEESidebarView.AVAILABILITY && (
                                                <DowntimeDisplayUnitsDropdown bigSize={true} />
                                            )}
                                    </div>
                                    {process.env.NODE_ENV.toLowerCase() !== "production" && tokenAvailable && (
                                        <LogoutButton />
                                    )}
                                </div>
                                <div className={classes.graphs}>
                                    <ActiveView />
                                </div>
                            </div>
                        )}
                        {(!machineStatus || store.isEventReportEditing || continueDowntimeDetailsInput) &&
                            failureDetailsStep !== FAILUREDETAILSSTEPS.PLCSHOWHOMEDURINGDOWNTIME && <FailureDetails />}
                    </Box>
                </div>
            </div>
        </div>
    );
};
